/**
 * FALLBACK ONLY. The Ruby Token mark is ONE hosted asset (spec 2026-09-27
 * D10) rendered through `<RubyToken>`; this inline drawing is what
 * `<RubyToken>` paints when that asset cannot be fetched (CDN down, asset not
 * yet uploaded to a stage), so a loader never silently disappears. Do not
 * import it anywhere else -- use `RubyGemMark` / `RubyToken`.
 *
 * THE RUBY TILL GEM -- the brand mark, straight off
 * `Branding/Ruby Till Branding-V2/03 Animation/Web and email/rubytill-logo-animated-*.svg`
 * (the 20-edge gem, 13 nodes, stroked #DC1A3C). Same geometry, same colour,
 * same line-by-line cut -- so the loader and the logo are one mark.
 *
 *  - `drawing` false -> the finished mark, static (logo use).
 *  - `drawing` true  -> the gem is cut edge by edge, the nodes pop, it holds,
 *                       fades, and cuts again: the loading state.
 *
 * Brand rules (README): never recolour, rotate, gradient or drop-shadow the
 * gem. The stroke is thickened only at small sizes so it stays visible --
 * 22 units in a 1024 box is under a pixel at 22px.
 */
import React from 'react';

const COLOR = '#DC1A3C';

/** [x1, y1, x2, y2] -- the brand file's g0..g19, in its draw order. */
const EDGES: Array<[number, number, number, number]> = [
  [345.7, 266.9, 678.3, 266.9], [678.3, 266.9, 799.3, 418.1], [799.3, 418.1, 648.1, 508.9],
  [648.1, 508.9, 375.9, 508.9], [375.9, 508.9, 224.7, 418.1], [224.7, 418.1, 345.7, 266.9],
  [432.1, 303.7, 591.9, 303.7], [591.9, 303.7, 641.6, 355.5], [641.6, 355.5, 591.9, 407.3],
  [591.9, 407.3, 432.1, 407.3], [432.1, 407.3, 382.4, 355.5], [382.4, 355.5, 432.1, 303.7],
  [641.6, 355.5, 799.3, 418.1], [591.9, 407.3, 648.1, 508.9], [432.1, 407.3, 375.9, 508.9],
  [382.4, 355.5, 224.7, 418.1], [799.3, 418.1, 512.0, 758.3], [648.1, 508.9, 512.0, 758.3],
  [375.9, 508.9, 512.0, 758.3], [224.7, 418.1, 512.0, 758.3],
];

/** n0..n12 */
const NODES: Array<[number, number]> = [
  [345.7, 266.9], [678.3, 266.9], [799.3, 418.1], [648.1, 508.9], [375.9, 508.9],
  [224.7, 418.1], [432.1, 303.7], [591.9, 303.7], [641.6, 355.5], [591.9, 407.3],
  [432.1, 407.3], [382.4, 355.5], [512.0, 758.3],
];

/** Square box around the gem, optically centred as the brand file centres it. */
const VIEW = 640;
const VIEWBOX = `192 193 ${VIEW} ${VIEW}`;

/*
  Small sizes. Scaling one stroke up to stay visible made the full gem a blob
  below 32px, so small sizes keep every edge and node but take their own
  weight and a tighter box -- the same values as the hosted ruby-token-sm /
  -xs files (Branding .../Web and email/generate-small-ruby-tokens.mjs).
*/
const SMALL_WEIGHTS = {
  sm: { sw: 34, r: 34 }, // 20-31px
  xs: { sw: 40, r: 38 }, // < 20px
} as const;
const SMALL_VIEWBOX = '190 190 644 644';

/*
  One cycle, looped. Every edge runs the same keyframes for the same period;
  only its delay differs -- so the stagger is identical on every loop.
    0-30%  cut in   | 30-82% hold | 82-100% fade out
*/
const CYCLE_S = 2.6;
const KEYFRAMES = `
@keyframes rtGemCut { 0% { stroke-dashoffset: var(--len); opacity: 1 } 30% { stroke-dashoffset: 0; opacity: 1 }
  82% { stroke-dashoffset: 0; opacity: 1 } 100% { stroke-dashoffset: 0; opacity: 0 } }
@keyframes rtGemPop { 0%, 22% { transform: scale(0); opacity: 0 } 34% { transform: scale(1.15); opacity: 1 }
  40%, 82% { transform: scale(1); opacity: 1 } 100% { transform: scale(1); opacity: 0 } }
@media (prefers-reduced-motion: reduce) { .rt-gem * { animation: none !important; stroke-dashoffset: 0 !important; opacity: 1 !important; transform: none !important } }
`;

const len = ([x1, y1, x2, y2]: [number, number, number, number]) => Math.hypot(x2 - x1, y2 - y1);

export interface RubyGemInlineProps {
  size?: number;
  drawing?: boolean;
  title?: string;
  style?: React.CSSProperties;
  /**
   * Stroke/fill. Ruby by default. Pass `currentColor` inside a coloured
   * button so the gem takes the button's text colour (a ruby gem on a ruby
   * button would vanish) -- the brand's on-red / on-black finishes.
   */
  color?: string;
}

export const RubyGemInline: React.FC<RubyGemInlineProps> = ({ size = 24, drawing = false, title, style, color = COLOR }) => {
  // Units per rendered pixel; at 32px+ never let a stroke fall under ~1.6px.
  const upp = VIEW / size;
  const small = size < 32 ? SMALL_WEIGHTS[size < 20 ? 'xs' : 'sm'] : null;
  const sw = small ? small.sw : Math.max(22, 1.6 * upp);
  const r = small ? small.r : Math.max(17.5, sw * 0.8);

  return (
    <svg
      className="rt-gem"
      viewBox={small ? SMALL_VIEWBOX : VIEWBOX}
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ flexShrink: 0, overflow: 'visible', ...style }}
    >
      {drawing && <style>{KEYFRAMES}</style>}
      {EDGES.map((e, i) => {
        const l = len(e);
        return (
          <line
            key={`e${i}`}
            x1={e[0]} y1={e[1]} x2={e[2]} y2={e[3]}
            fill="none" stroke={color} strokeWidth={sw}
            strokeLinecap="round" strokeLinejoin="round"
            style={drawing ? ({
              ['--len' as any]: l,
              strokeDasharray: l,
              strokeDashoffset: l,
              animation: `rtGemCut ${CYCLE_S}s ${(i * 0.045).toFixed(3)}s cubic-bezier(.2,.8,.3,1) infinite`,
            } as React.CSSProperties) : undefined}
          />
        );
      })}
      {NODES.map(([cx, cy], i) => (
        <circle
          key={`n${i}`}
          cx={cx} cy={cy} r={r} fill={color}
          style={drawing ? {
            transformBox: 'fill-box', transformOrigin: 'center', opacity: 0,
            animation: `rtGemPop ${CYCLE_S}s ${(0.1 + i * 0.03).toFixed(3)}s cubic-bezier(.3,1.5,.5,1) infinite`,
          } : undefined}
        />
      ))}
    </svg>
  );
};

export const RUBY_GEM_COLOR = COLOR;

export default RubyGemInline;
